import React, { useMemo } from 'react';
import { Button } from '../Button/Button';
import { Paper } from '../Paper/Paper';
import { FormLabel } from '../Form/FormLabel';
import { BpDesignSystem } from '../../types';
import { mergeClassNames } from '../../utils/cssClassNameHelper';
import { Grid } from '../Grid/Grid';
import { FormHelperText } from '../Form/FormHelperText';
import { styled } from '../../styles';
import { Box } from '../Box/Box';
import { Keys } from '../../utils/keysUtil';
import { tokenToPx } from '../../setup/theme/impl/getToken';

type NumberPickerProps = BpDesignSystem.InputProps<{
  model: number | undefined | null;
  onChange(value: number): void;
  numberOfElements: number;
  elementsInRow?: number;
  countFromOne?: boolean;
  fullWidth?: boolean;
}>;

export const NumberPicker: React.FC<NumberPickerProps> = ({
  numberOfElements,
  model,
  onChange,
  elementsInRow = 7,
  countFromOne = true,
  fullWidth = false,
  error,
  errorText,
  helperText,
  label,
  dataTest,
  className,
  style,
  sx,
  required,
  disabled
}) => {
  const elements = useMemo(() => {
    const result = Array.from(Array(numberOfElements).keys());
    if (!countFromOne) {
      return result;
    }
    return result.map((it) => it + 1);
  }, [numberOfElements, countFromOne]);

  const inRow = elementsInRow > 0 ? elementsInRow : 1;
  const width = fullWidth ? '100%' : inRow * 36 + 8;
  return (
    <Box className={className} style={style} sx={sx} dataTest={dataTest}>
      <FormLabel required={required} disabled={disabled} error={error} dataTest={`${dataTest}-label`}>
        {label}
      </FormLabel>
      <Paper
        variant="outlined"
        className={mergeClassNames('number-picker', { error })}
        sx={{ padding: 2, width }}
        onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
          if (disabled) {
            return;
          }
          const elementClassname = (e.target as HTMLDivElement).className;
          const elementNumber = parseInt(elementClassname.split('item-')[1]?.split(' ')[0] ?? '1', 10);
          if (Keys.oneOf(e, 'ArrowRight')) {
            findItemAndFocus(e, elements, elementNumber + 1);
          }
          if (Keys.oneOf(e, 'ArrowLeft')) {
            findItemAndFocus(e, elements, elementNumber - 1);
          }
          if (Keys.oneOf(e, 'ArrowUp')) {
            findItemAndFocus(e, elements, elementNumber - inRow);
          }
          if (Keys.oneOf(e, 'ArrowDown')) {
            findItemAndFocus(e, elements, elementNumber + inRow);
          }
        }}
      >
        <Grid container columns={inRow} justifyItems="center">
          {elements.map((it, index) => {
            const hasModel = typeof model === 'number';
            return (
              <Grid key={it} size={1}>
                <NumberItem
                  id={it}
                  active={it === model}
                  onClick={onChange}
                  disabled={disabled}
                  focus={hasModel ? it === model : index === 0}
                />
              </Grid>
            );
          })}
        </Grid>
      </Paper>
      <FormHelperText helperText={helperText} errorText={errorText} error={error} />
    </Box>
  );
};

function findItemAndFocus(e: React.KeyboardEvent<HTMLDivElement>, elements: number[], nextElementNumber: number) {
  if (elements.includes(nextElementNumber)) {
    (e.currentTarget.querySelector(`.item-${nextElementNumber}`) as HTMLDivElement)?.focus();
  }
  e.stopPropagation();
  e.preventDefault();
}

const NumberItem: React.FC<{
  id: number;
  focus: boolean;
  active: boolean;
  disabled?: boolean;
  onClick: (id: number) => void;
}> = ({ active, onClick, id, focus, disabled }) => {
  return (
    <StyledButton
      color="primary"
      tabIndex={focus ? 0 : -1}
      centerRipple
      dataTest={`number-item-${id}`}
      disabled={disabled}
      onClick={() => onClick(id)}
      className={mergeClassNames(`number-item item-${id}`, { active: active })}
    >
      <span>{id}</span>
    </StyledButton>
  );
};

const StyledButton = styled(Button)`
    && {
      transition: all 0.2s ease-in-out;
      height: 36px;
      min-height: 36px;
      width: 36px;
      min-width: 36px;
      line-height: 36px;
      border-radius: 50%;
      padding: 0;
      font-size: ${tokenToPx('bodySmallFontSize')};
      color: ${({ theme }) => theme.tokens.colorOnSurface};

      .MuiTouchRipple-root {
        top: 2px;
        right: 2px;
        bottom: 2px;
        left: 2px;
      }

      .MuiTouchRipple-child {
        background-color: ${({ theme }) => theme.tokens.statePrimaryFocusStateLayerColor};
      }
        
      &.Mui-disabled {
          color: rgba(0, 0, 0, 0.26);
      }  

      &:hover::before {
        border-radius: 50%;
        background-color: transparent;
      }

      &:active,
      &:hover {
        background-color: ${({ theme }) => theme.tokens.statePrimaryHoverStateLayerColor};
      }

      &.active {
        background-color: ${({ theme }) => theme.tokens.colorPrimary};
        color: ${({ theme }) => theme.tokens.colorOnPrimary};
        .MuiTouchRipple-child {
            background-color: ${({ theme }) => theme.tokens.colorSurface};
        }
        &.Mui-disabled {
            color: ${({ theme }) => theme.tokens.stateOnSurfaceDisabledContentStateLayerColor};
            background-color: ${({ theme }) => theme.tokens.stateOnSurfaceDisabledContainerStateLayerColor};
        }
      }
      &:focus:not(.active) {
        background-color: transparent;

        .MuiTouchRipple-ripple.MuiTouchRipple-rippleVisible {
          animation-name: fadeInAnimation;
          opacity: 1;
        }
      }
    }
    @keyframes fadeInAnimation {
      0% {
        transform: scale(0);
        opacity: 0.1;
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
`;
